import { CenteredLazyLoading } from "@/controls/LazyLoading";
import {
	Box,
	LinearProgress,
	Paper,
	Table,
	TableContainer,
} from "@mui/material";
import { useEffect, useState } from "react";
import type { WaterBillTarget } from "../../models/WaterBilling";
import type { WaterBillGridRow, WaterBillPeriod } from "../../utils/billRows";
import { CustomTableBody } from "./CustomTableBody";
import { CustomTableHeaders } from "./CustomTableHeaders";

interface WaterBillsTableProps {
	rows: WaterBillGridRow[];
	targets: WaterBillTarget[];
	periods: WaterBillPeriod[];
	isLoading: boolean;
	onBillSave: (billRows: WaterBillGridRow[]) => void;
	onBillDelete: (billId: number) => void;
}

export const WaterBillsTable = ({
	rows,
	targets,
	periods,
	isLoading,
	onBillSave,
	onBillDelete,
}: WaterBillsTableProps) => {
	const [editingBillId, setEditingBillId] = useState<number | null>(null);
	const [draftRows, setDraftRows] = useState(rows);

	useEffect(() => {
		setDraftRows(rows);
		setEditingBillId(null);
	}, [rows]);

	if (isLoading && draftRows.length === 0) {
		return (
			<Paper elevation={2} sx={{ height: "65vh", p: 1 }}>
				<CenteredLazyLoading />
			</Paper>
		);
	}

	return (
		<Box sx={{ position: "relative" }}>
			{isLoading && draftRows.length > 0 && (
				<LinearProgress
					sx={{
						borderRadius: "4px 4px 0 0",
						left: 0,
						pointerEvents: "none",
						position: "absolute",
						right: 0,
						top: 0,
						zIndex: 3,
					}}
				/>
			)}
			<TableContainer
				component={Paper}
				elevation={2}
				sx={{
					height: "70vh",
					maxHeight: { xs: 460, lg: "calc(100vh - 260px)" },
				}}
			>
				<Table
					size="small"
					stickyHeader
					sx={{
						"& td": {
							borderRight: "1px solid",
							borderColor: "divider",
							boxSizing: "border-box",
							fontSize: 11,
							height: 30,
							lineHeight: 1.2,
							px: 0.5,
							py: 0,
						},
						tableLayout: "fixed",
					}}
				>
					<colgroup>
						{[
							"5%",
							"4%",
							"6%",
							"6%",
							"6%",
							"6%",
							"5%",
							"5%",
							"5%",
							"5%",
							"6%",
							"6%",
							"5%",
							"6%",
							...Array.from({ length: targets.length * 3 }, () => "4.5%"),
							"6%",
						].map((width, index) => (
							<col key={index} style={{ width }} />
						))}
					</colgroup>
					<CustomTableHeaders targets={targets} />
					<CustomTableBody
						rows={rows}
						draftRows={draftRows}
						targets={targets}
						editingBillId={editingBillId}
						setEditingBillId={setEditingBillId}
						setDraftRows={setDraftRows}
						periods={periods}
						onBillSave={onBillSave}
						onBillDelete={onBillDelete}
					/>
				</Table>
			</TableContainer>
		</Box>
	);
};
